import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import RecordingsList from '../components/RecordingsList';
import Avatar from '../design/Avatar';
import Button from '../design/Button';
import Card from '../design/Card';
import EmptyState from '../design/EmptyState';
import ErrorState from '../design/ErrorState';
import LoadingState from '../design/LoadingState';
import StatusChip from '../design/StatusChip';
import {
  getClass,
  getRecordingsForStudent,
  getStudent,
  getStudentProgress,
} from '../utils/schoolFirestore';
import { formatPasukRef, statusFromScore } from '../utils/pasuk';
import type { Recording, SchoolClass, Student, StudentProgress } from '../types';

interface StudentDetails {
  student: Student;
  schoolClass: SchoolClass | null;
  progress: StudentProgress | null;
  recordings: Recording[];
}

const TeacherStudentPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { studentId } = useParams<{ studentId: string }>();
  const [details, setDetails] = useState<StudentDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    if (!studentId) return;
    setLoading(true);
    setError(false);
    try {
      const student = await getStudent(studentId);
      if (!student) throw new Error('Student not found');
      const [schoolClass, progress, recordings] = await Promise.all([
        getClass(student.classId),
        getStudentProgress(student.id),
        getRecordingsForStudent(student.schoolId, student.id),
      ]);
      setDetails({ student, schoolClass, progress, recordings });
    } catch (err) {
      console.error('[school] Failed to load student details', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    load();
  }, [load]);

  const back = (): void => navigate(details ? `/?class=${details.student.classId}` : '/');

  if (loading) return <LoadingState />;
  if (error || !details) return <ErrorState onRetry={load} />;

  const { student, schoolClass, progress, recordings } = details;
  const name = `${student.firstName} ${student.lastName}`;
  // Pesukim the student practiced, newest first; the key format is "<book>_<chapter>_<pasuk>".
  const pesukim = Object.entries(progress?.pesukim ?? {})
    .map(([key, p]) => {
      const [book, chapter, pasuk] = key.split('_');
      return { key, ref: { book, chapter: Number(chapter), pasuk: Number(pasuk) }, p };
    })
    .sort((a, b) => (b.p.lastAt?.toMillis() ?? 0) - (a.p.lastAt?.toMillis() ?? 0));

  return (
    <>
      <Button
        variant="ghost"
        icon="chevron"
        onClick={back}
        iconClassName="ltr:rotate-180"
        className="mb-4"
      >
        {t('school_back_to_class')}
      </Button>

      <div className="mb-6 flex flex-wrap items-center gap-4">
        <Avatar name={name} size="lg" />
        <div>
          <h1 className="text-2xl font-extrabold text-school-ink sm:text-3xl">{name}</h1>
          <p className="text-sm text-school-muted">
            {schoolClass ? t('school_class_name', { className: schoolClass.name }) : ''}
            {' · '}
            {t('school_personal_code')}:{' '}
            <span className="font-mono" dir="ltr">
              {student.loginCode}
            </span>
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card title={t('school_progress_by_pasuk')}>
          {pesukim.length === 0 ? (
            <EmptyState title={t('school_no_progress')} />
          ) : (
            <ul className="divide-y divide-school-line">
              {pesukim.map(({ key, ref, p }) => (
                <li key={key} className="flex items-center justify-between gap-3 py-3 text-sm">
                  <div>
                    <div className="font-semibold">{formatPasukRef(ref, i18n.language)}</div>
                    <div className="text-xs text-school-muted">
                      {t('school_attempts_count', { n: p.attempts })}
                    </div>
                  </div>
                  <StatusChip
                    status={statusFromScore(p.bestScore, p.attempts)}
                    suffix={String(p.bestScore)}
                  />
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card title={t('school_recordings')} className="lg:col-span-2">
          <RecordingsList recordings={recordings} showPasuk showExplanation />
        </Card>
      </div>
    </>
  );
};

export default TeacherStudentPage;
